/**
 * El punto ÚNICO donde se resuelve el destinatario de una campaña.
 *
 * Antes había dos caminos con contratos distintos alimentando el mismo motor:
 * la lista de contactos producía `phone: c.remitente` —que en los chats de WhatsApp
 * contiene el NOMBRE— y el CSV producía dígitos. El primer envío real salió con
 * `to: "Rodrigo"` y Meta respondió 131009 «The phone number is malformed».
 *
 * Ahora los dos pasan por aquí y producen la MISMA forma, o no producen nada.
 *
 * ESTO NO ES LA AUTORIDAD. `libphonenumber-js` no está en el frontend, así que aquí
 * sólo se acepta o se rechaza para no dejar seleccionar lo que va a fallar y para dar
 * el motivo en el acto. Quien decide de verdad es `executeCampaign`, que normaliza con
 * `phoneNormalizer` antes de componer el `to`. Es el mismo reparto que
 * `plantillaDesdeFormulario.js`: el front guía, el backend manda.
 *
 * LAS DOS REGLAS SON DISTINTAS PORQUE LAS FUENTES SON DISTINTAS
 * ────────────────────────────────────────────────────────────
 * CHAT · el campo `telefono` lo escribe META, en E.164 sin el '+'. Se le antepone el
 *   '+' y se valida. Eso NO es inventar: recupera información que estaba, de una
 *   fuente conocida en un formato conocido. Nunca se adivina el país.
 *
 *   Medido el 28-ago-2026 sobre los tres chats reales de producción:
 *     normalizePhone('56981363182', null)  -> null
 *     normalizePhone('56995843466', null)  -> null
 *     normalizePhone('56956285967', null)  -> null
 *     normalizePhone('+56981363182', null) -> '56981363182'   (los tres, con '+')
 *   Sin el '+' los tres destinatarios reales de la organización serían rechazados.
 *   El hint de país que `normalizePhone` admite NO es resoluble aquí: la organización
 *   no tiene zona horaria y el único bot la tiene en null.
 *
 * CSV · lo teclea una persona y puede ser un número LOCAL. No hay ninguna fuente que
 *   garantice E.164, así que se exige código de país —con '+' o sin él— y lo que no
 *   lo traiga se rechaza diciendo qué formato se espera.
 */

/** Sólo formato: espacios, guiones y paréntesis. El '+' se CONSERVA. */
function limpiarFormato(valor) {
  return String(valor ?? '').trim().replace(/[\s\-()]/g, '');
}

/**
 * Longitud mínima para dar por presente un código de país en el CSV.
 *
 * Es una HEURÍSTICA, y se declara como tal: sin `libphonenumber-js` el frontend no
 * puede saber si un número trae prefijo. Un móvil local suele quedar por debajo (en
 * Chile son 9 dígitos) y con código de país lo supera. Puede dejar pasar algún caso
 * raro — el backend lo rechazará con `isValid()`, que es la comprobación de verdad.
 */
const MIN_DIGITOS_CON_PAIS = 10;

const MOTIVOS = {
  SIN_TELEFONO: 'sin_telefono',
  NO_ES_NUMERO: 'no_es_numero',
  SIN_CODIGO_PAIS: 'sin_codigo_pais',
};

/**
 * Resuelve el destinatario a partir de un documento de chat.
 *
 * @returns {{ok: true, destinatario: {nombre: string, phone: string}} |
 *           {ok: false, motivo: string}}
 */
export function destinatarioDesdeChat(chat) {
  const nombre = chat?.nombre || chat?.remitente || '';
  const crudo = limpiarFormato(chat?.telefono);

  // OJO: `remitente` NO es un teléfono. Es el nombre, y usarlo aquí fue el defecto.
  if (!crudo) return { ok: false, motivo: MOTIVOS.SIN_TELEFONO };
  if (!/^\+?\d+$/.test(crudo)) return { ok: false, motivo: MOTIVOS.NO_ES_NUMERO };

  // Regla del chat: Meta lo escribe en E.164 sin '+'. Se lo devolvemos.
  const conMas = crudo.startsWith('+') ? crudo : `+${crudo}`;
  if (conMas.length < 9) return { ok: false, motivo: MOTIVOS.NO_ES_NUMERO };

  return { ok: true, destinatario: { nombre, phone: conMas } };
}

/**
 * Resuelve el destinatario a partir de una fila de CSV.
 *
 * @returns {{ok: true, destinatario: {nombre, phone, extraFields}} |
 *           {ok: false, motivo: string}}
 */
export function destinatarioDesdeCsv({ telefono, nombre = '', extraFields = {} } = {}) {
  const crudo = limpiarFormato(telefono);

  if (!crudo) return { ok: false, motivo: MOTIVOS.SIN_TELEFONO };
  if (!/^\+?\d+$/.test(crudo)) return { ok: false, motivo: MOTIVOS.NO_ES_NUMERO };

  const digitos = crudo.replace(/^\+/, '');
  // Con '+' el usuario ya declaró el prefijo. Sin él, se exige longitud suficiente.
  if (!crudo.startsWith('+') && digitos.length < MIN_DIGITOS_CON_PAIS) {
    return { ok: false, motivo: MOTIVOS.SIN_CODIGO_PAIS };
  }
  if (digitos.length > 15) return { ok: false, motivo: MOTIVOS.NO_ES_NUMERO };

  return {
    ok: true,
    destinatario: { nombre, phone: `+${digitos}`, extraFields },
  };
}

export { MOTIVOS, MIN_DIGITOS_CON_PAIS };
